import {
  WidgetProperty,
  WidgetDefinition,
  WidgetPropertyDefinition,
} from "src/dynamic-view-renderer/Widgets/Widget.typings";
import { BlacklistedCaseIdentifier } from "src/types/case";
import { ClientSchema } from "src/types/clientSchema";
import { Element } from "src/types/element";
import { Identifier } from "src/types/identifier";
import { PropertyValue } from "src/types/property";

import {
  getDisplayedPropertiesFromClientSchema,
  getPropertiesFromClientSchema,
} from "./getPropertiesFromSchema";
import { CASE_TYPE_CODE } from "./widgetProperties.constants";
import {
  useMapMultiValuePropertyToWidgetProperty,
  useMapSingleValuePropertyToWidgetProperty,
} from "./widgetProperties.mapping";
import { getElementByPath, isDisplayedPropertyValid } from "./widgetProperties.utils";

// --- Determine displayed widget properties based on widget config and schema ---
const determineDisplayedProperties = (
  widgetDefinition: WidgetDefinition | undefined | null,
  itemSchema: ClientSchema | undefined | null,
  itemCode: string
): WidgetPropertyDefinition[] => {
  const config = widgetDefinition?.configuration;
  const exclude = config?.excludeProperties ?? [];

  const properties =
    config?.displayedProperties === null
      ? getPropertiesFromClientSchema(config?.elementPath, itemCode, itemSchema)
      : getDisplayedPropertiesFromClientSchema(widgetDefinition, CASE_TYPE_CODE, itemSchema);

  return properties?.filter((prop) => !exclude.includes(prop.property ?? "")) ?? [];
};

// --- Main hook to generate widget properties for rendering ---
const useGetWidgetProperties = () => {
  const mapSingleValuePropertyToWidgetProperty = useMapSingleValuePropertyToWidgetProperty();
  const mapMultiValuePropertyToWidgetProperty = useMapMultiValuePropertyToWidgetProperty();

  // --- Unified mapping logic for single or multi value properties ---
  const mapToWidgetProperty = (
    {
      displayedPropertyDefinition,
      propertiesOrIdentifiersData,
      widgetDefinition,
      blacklistedIdentifiers,
      comparisonMapping,
      elements,
    }: Parameters<ReturnType<typeof useMapSingleValuePropertyToWidgetProperty>>[0],
    multiple: boolean
  ): WidgetProperty | null | undefined => {
    return multiple
      ? mapMultiValuePropertyToWidgetProperty({
          displayedPropertyDefinition,
          propertiesOrIdentifiersData,
          widgetDefinition,
          blacklistedIdentifiers,
          comparisonMapping,
          elements,
        })
      : mapSingleValuePropertyToWidgetProperty({
          displayedPropertyDefinition,
          propertiesOrIdentifiersData,
          widgetDefinition,
          blacklistedIdentifiers,
          comparisonMapping,
          elements,
        });
  };

  const getWidgetProperties = ({
    widgetDefinition,
    data,
    propertyDefinitions,
    itemSchema,
  }: {
    itemSchema?: ClientSchema | undefined | null;
    widgetDefinition?: WidgetDefinition | null;
    data?: {
      item: {
        propertyValues?: PropertyValue[];
        elements?: Element[];
        code: string;
        blacklistedIdentifiers?: BlacklistedCaseIdentifier[];
      };
    };
    propertyDefinitions?: WidgetPropertyDefinition[];
  }): WidgetProperty[] => {
    if (!data?.item?.propertyValues && !data?.item?.elements) return [];

    const displayedProperties = determineDisplayedProperties(
      widgetDefinition,
      itemSchema,
      data.item.code
    );
    const elements = data.item?.elements ?? [];
    const caseProperties = data.item?.propertyValues ?? [];
    const globalElementPath = widgetDefinition?.configuration?.elementPath;
    const comparisonMapping = widgetDefinition?.configuration?.comparisonMapping;

    return displayedProperties.reduce<WidgetProperty[]>((acc, displayedProperty) => {
      const { isValid, propertyDefinition, hasMultipleProperties } = isDisplayedPropertyValid(
        displayedProperty,
        propertyDefinitions
      );

      if (!isValid) return acc;

      const elementPath = propertyDefinition.elementPath ?? globalElementPath;

      let propertiesOrIdentifiersData: Identifier[] | PropertyValue[] = [];

      if (elementPath) {
        const element = getElementByPath(elements, elementPath);
        if (propertyDefinition.type === "IdentifierGraph") {
          propertiesOrIdentifiersData =
            element && "identifiers" in element ? element.identifiers : [];
        } else {
          propertiesOrIdentifiersData =
            element && "properties" in element ? element.properties : [];
        }
      }

      if (!elementPath) {
        propertiesOrIdentifiersData = caseProperties ?? [];
      }

      const widgetProp = mapToWidgetProperty(
        {
          displayedPropertyDefinition: propertyDefinition,
          propertiesOrIdentifiersData,
          widgetDefinition,
          blacklistedIdentifiers: data.item?.blacklistedIdentifiers,
          comparisonMapping,
          elements,
        },
        hasMultipleProperties
      );

      return widgetProp ? [...acc, widgetProp] : acc;
    }, []);
  };

  return getWidgetProperties;
};

export default useGetWidgetProperties;
